import React, { useEffect, useMemo, useState } from 'react';
import { parseFitment, etiquetaFitment } from '@/components/VehicleFitmentInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tip } from '../components/ui/tip';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ProductCombobox from '../components/ProductCombobox';
import { DatePicker } from '../components/ui/date-picker';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';

type ProductRecord = {
  id: string;
  name: string;
  description?: string | null;
  sku?: string | null;
  category?: string | null;
  status?: string | null;
  brand?: string | null;
  model?: string | null;
  tireMeasure?: string | null;
  loadIndex?: string | null;
  speedIndex?: string | null;
  vehicleFitment?: string | null;
  costPrice?: number;
  listPrice?: number;
  salePrice?: number;
  stock?: number;
  minStock?: number;
  mainSupplierName?: string | null;
  viscosity?: string | null;
  oilBaseType?: string | null;
  capacity?: string | null;
  apiSpec?: string | null;
  lubricantType?: string | null;
  partNumber?: string | null;
  estimatedDuration?: number | null;
  requiredRole?: string | null;
  requiredSkills?: string[] | string | null;
};

type SupplierPriceRecord = {
  id: string;
  providerName: string;
  costPrice: number;
  listPrice?: number;
  utilityPercent?: number;
  pricingStrategy?: string | null;
  supplierSku?: string | null;
  isFavorite?: number;
  updatedAt?: string;
};

type CostHistoryRecord = {
  id: string;
  costPrice: number;
  previousCostPrice?: number | null;
  providerName?: string | null;
  createdAt: string;
};

type SaleHistoryRecord = {
  id: string;
  salePrice: number;
  previousSalePrice?: number | null;
  createdAt: string;
};

type SalesAnalyticsPoint = {
  date: string;
  units: number;
  revenue: number;
  remitos: number;
};

type SalesAnalyticsPayload = {
  series: SalesAnalyticsPoint[];
  totals?: {
    units?: number;
    revenue?: number;
    remitos?: number;
  };
};

const formatMoney = (value: number | undefined | null) =>
  Number(value || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
const formatPercent = (value: number | undefined | null) => `${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const formatMoneyOptional = (value: number | undefined | null) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return '-';
  return formatMoney(parsed);
};

const normalizeProvider = (value: string | null | undefined) => String(value || '').trim().toLowerCase();
const toMillis = (value: string | undefined | null) => {
  const ms = value ? new Date(value).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
};

type PriceChartPoint = {
  ts: number;
  date: string;
  cost: number;
  sale: number;
};

type KpiCard = {
  label: string;
  value: string;
  hint: string;
};

const SERIES_META: Record<'cost' | 'sale', { label: string; color: string }> = {
  cost: { label: 'Costo', color: 'hsl(var(--chart-1))' },
  sale: { label: 'Precio venta', color: 'hsl(var(--chart-2))' }
};

const ProductView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateProduct = (location.state as any)?.product as ProductRecord | undefined;
  const [loading, setLoading] = useState(!stateProduct);
  const [product, setProduct] = useState<ProductRecord | null>(stateProduct || null);
  // Equivalencias: "este filtro reemplaza a este otro". Relación simétrica,
  // se administra desde cualquiera de los dos productos.
  const [equivalences, setEquivalences] = useState<any[]>([]);
  const [equivCatalog, setEquivCatalog] = useState<any[]>([]);
  const [equivNote, setEquivNote] = useState('');
  const [savingEquiv, setSavingEquiv] = useState(false);
  const [supplierPrices, setSupplierPrices] = useState<SupplierPriceRecord[]>([]);
  const [costHistory, setCostHistory] = useState<CostHistoryRecord[]>([]);
  const [saleHistory, setSaleHistory] = useState<SaleHistoryRecord[]>([]);
  const [rangeDays, setRangeDays] = useState<30 | 90 | 180 | 365>(90);
  const [rangeMode, setRangeMode] = useState<'preset' | 'custom'>('preset');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [focusSeries, setFocusSeries] = useState<'all' | 'cost' | 'sale'>('all');
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]); // vac\u00edo = todos
  const [salesMode, setSalesMode] = useState<'units' | 'revenue'>('units');
  const [salesAnalytics, setSalesAnalytics] = useState<SalesAnalyticsPayload>({ series: [] });
  const [analytics90d, setAnalytics90d] = useState<SalesAnalyticsPayload>({ series: [] });
  const [analyticsHistory, setAnalyticsHistory] = useState<SalesAnalyticsPayload>({ series: [] });

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      if (!stateProduct) setLoading(true);
      try {
        const [productRes, suppliersRes, providersRes] = await Promise.all([
          axios.get(`/api/products/${id}`),
          axios.get(`/api/products/${id}/supplier-prices`),
          axios.get(`/api/products/${id}/history-providers`)
        ]);
        setProduct(productRes.data?.product || null);
        setSupplierPrices(suppliersRes.data?.supplierPrices || []);
        setAvailableProviders(providersRes.data?.providers || []);
      } catch (error) {
        console.error('Error loading product view:', error);
        toast.error('No se pudo cargar la ficha del producto');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      try {
        const providersParam = selectedProviders.length > 0
          ? `&providers=${encodeURIComponent(selectedProviders.join(','))}`
          : '';
        const [historyRes, saleHistoryRes] = await Promise.all([
          axios.get(`/api/products/${id}/cost-history?limit=200${providersParam}`),
          axios.get(`/api/products/${id}/sale-history?limit=200${providersParam}`)
        ]);
        setCostHistory(historyRes.data?.history || []);
        setSaleHistory(saleHistoryRes.data?.history || []);
      } catch (error) {
        console.error('Error loading price history:', error);
      }
    };
    run();
  }, [id, selectedProviders]);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      try {
        const params: Record<string, string | number> = {};
        if (rangeMode === 'custom') {
          if (customFrom) params.from = customFrom;
          if (customTo) params.to = customTo;
          if (!customFrom && !customTo) params.days = rangeDays;
        } else {
          params.days = rangeDays;
        }

        const response = await axios.get(`/api/products/${id}/sales-analytics`, { params });
        setSalesAnalytics({
          series: response.data?.series || [],
          totals: response.data?.totals || {}
        });
      } catch (error) {
        console.error('Error loading product sales analytics:', error);
        setSalesAnalytics({ series: [] });
      }
    };
    run();
  }, [id, rangeMode, rangeDays, customFrom, customTo]);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      try {
        const [analytics90Res, analyticsHistoryRes] = await Promise.all([
          axios.get(`/api/products/${id}/sales-analytics`, { params: { days: 90 } }),
          axios.get(`/api/products/${id}/sales-analytics`, { params: { days: 3650 } })
        ]);
        setAnalytics90d({
          series: analytics90Res.data?.series || [],
          totals: analytics90Res.data?.totals || {}
        });
        setAnalyticsHistory({
          series: analyticsHistoryRes.data?.series || [],
          totals: analyticsHistoryRes.data?.totals || {}
        });
      } catch (error) {
        console.error('Error loading product KPIs analytics:', error);
        setAnalytics90d({ series: [], totals: {} });
        setAnalyticsHistory({ series: [], totals: {} });
      }
    };
    run();
  }, [id]);

  const mergedProviderRows = (() => {
    if (!product) return [];
    const rows = [...supplierPrices];
    const knownKeys = new Set(rows.map((row) => `${normalizeProvider(row.providerName)}|${Number(row.costPrice || 0).toFixed(2)}`));

    for (const entry of costHistory) {
      const cost = Number(entry.costPrice || 0);
      if (cost <= 0) continue;
      const providerName = String(entry.providerName || '').trim() || 'Proveedor historico';
      const key = `${normalizeProvider(providerName)}|${cost.toFixed(2)}`;
      const exactCostKnown = rows.some((row) => Number(row.costPrice || 0).toFixed(2) === cost.toFixed(2));
      if (!knownKeys.has(key) && !exactCostKnown) {
        rows.push({
          id: `history-${entry.id}`,
          providerName,
          costPrice: cost,
          isFavorite: 0,
          updatedAt: entry.createdAt
        });
        knownKeys.add(key);
      }
    }

    const latestByProvider = new Map<string, SupplierPriceRecord>();
    for (const row of rows) {
      const key = normalizeProvider(row.providerName) || '__sin_proveedor__';
      const existing = latestByProvider.get(key);
      if (!existing || toMillis(row.updatedAt) >= toMillis(existing.updatedAt)) {
        latestByProvider.set(key, row);
      }
    }

    return Array.from(latestByProvider.values()).sort((a, b) => Number(a.costPrice || 0) - Number(b.costPrice || 0));
  })();

  const inferHistoryProvider = (entry: CostHistoryRecord) =>
    String(entry.providerName || '').trim() ||
    mergedProviderRows.find((row) => Number(row.costPrice || 0) === Number(entry.costPrice || 0))?.providerName ||
    '';

  const vigenteHistoryId = (() => {
    if (!product) return null;
    const currentProvider = normalizeProvider(product.mainSupplierName);
    const currentCost = Number(product.costPrice || 0);
    const candidates = costHistory
      .map((entry) => ({ entry, provider: normalizeProvider(inferHistoryProvider(entry)) }))
      .filter((item) => Number(item.entry.costPrice || 0) === currentCost && item.provider === currentProvider)
      .sort((a, b) => toMillis(b.entry.createdAt) - toMillis(a.entry.createdAt));
    return candidates[0]?.entry.id || null;
  })();

  const chartData = useMemo<PriceChartPoint[]>(() => {
    if (!product) return [];
    const now = new Date();
    const presetCutoffMs = now.getTime() - rangeDays * 24 * 60 * 60 * 1000;
    const customFromMs = customFrom ? new Date(`${customFrom}T00:00:00`).getTime() : null;
    const customToMs = customTo ? new Date(`${customTo}T23:59:59`).getTime() : null;
    const sortedCostHistory = [...costHistory].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const sortedSaleHistory = [...saleHistory].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const costEvents = sortedCostHistory
      .map((entry) => {
        const ts = new Date(entry.createdAt).getTime();
        if (!Number.isFinite(ts)) return null;
        return { ts, value: Number(entry.costPrice || 0) };
      })
      .filter(Boolean) as Array<{ ts: number; value: number }>;

    // Si la primera entrada tiene un previousCostPrice distinto > 0, prepend un punto sintetico
    // 1ms antes con ese valor para que la grafica muestre la transicion (no quede plana).
    if (sortedCostHistory.length > 0) {
      const firstCost = sortedCostHistory[0];
      const firstCostTs = new Date(firstCost.createdAt).getTime();
      const prevCost = Number(firstCost.previousCostPrice || 0);
      const newCost = Number(firstCost.costPrice || 0);
      if (Number.isFinite(firstCostTs) && prevCost > 0 && prevCost !== newCost) {
        costEvents.unshift({ ts: firstCostTs - 1, value: prevCost });
      }
    }

    const saleEvents = sortedSaleHistory
      .map((entry) => {
        const ts = new Date(entry.createdAt).getTime();
        if (!Number.isFinite(ts)) return null;
        return { ts, value: Number(entry.salePrice || 0) };
      })
      .filter(Boolean) as Array<{ ts: number; value: number }>;

    if (sortedSaleHistory.length > 0) {
      const firstSale = sortedSaleHistory[0];
      const firstSaleTs = new Date(firstSale.createdAt).getTime();
      const prevSale = Number(firstSale.previousSalePrice || 0);
      const newSale = Number(firstSale.salePrice || 0);
      if (Number.isFinite(firstSaleTs) && prevSale > 0 && prevSale !== newSale) {
        saleEvents.unshift({ ts: firstSaleTs - 1, value: prevSale });
      }
    }

    const nowTs = now.getTime();
    costEvents.push({ ts: nowTs, value: Number(product.costPrice || 0) });
    saleEvents.push({ ts: nowTs, value: Number(product.salePrice || 0) });

    // Agrupar eventos que ocurren dentro de la misma "ventana" (1 minuto) en el mismo
    // punto de timeline para evitar etiquetas duplicadas en el eje X cuando cost y sale
    // se actualizan con milisegundos de diferencia.
    const BUCKET_MS = 60 * 1000;
    const bucketize = (ts: number) => Math.round(ts / BUCKET_MS) * BUCKET_MS;
    const timeline = Array.from(
      new Set([
        ...costEvents.map((e) => bucketize(e.ts)),
        ...saleEvents.map((e) => bucketize(e.ts))
      ])
    ).sort((a, b) => a - b);

    const getValueAtTs = (
      events: Array<{ ts: number; value: number }>,
      ts: number,
      initialFallback: number,
      tailFallback: number
    ) => {
      let value = events.length ? Number(events[0].value || 0) : initialFallback;
      for (const event of events) {
        if (event.ts <= ts) value = event.value;
        else break;
      }
      if (!Number.isFinite(Number(value))) return tailFallback;
      return Number(value || 0);
    };

    return timeline
      .map((ts) => ({
        ts,
        date: new Date(ts).toLocaleDateString('es-AR'),
        cost: getValueAtTs(
          costEvents,
          ts,
          Number(product.costPrice || 0),
          Number(product.costPrice || 0)
        ),
        sale: getValueAtTs(
          saleEvents,
          ts,
          Number(product.salePrice || 0),
          Number(product.salePrice || 0)
        )
      }))
      .sort((a, b) => a.ts - b.ts)
      .filter((point) => {
        if (rangeMode === 'custom') {
          if (customFromMs && point.ts < customFromMs) return false;
          if (customToMs && point.ts > customToMs) return false;
          return true;
        }
        return point.ts >= presetCutoffMs;
      });
  }, [costHistory, saleHistory, product, rangeDays, rangeMode, customFrom, customTo]);

  const trendTarget = useMemo(() => {
    const key: 'cost' | 'sale' =
      focusSeries === 'sale'
        ? 'sale'
        : 'cost';
    const nounByKey: Record<'cost' | 'sale', string> = {
      cost: 'el costo',
      sale: 'el precio de venta'
    };
    return { key, noun: nounByKey[key] };
  }, [focusSeries]);

  const trendStats = useMemo(() => {
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    const firstValue = Number(first?.[trendTarget.key] || 0);
    const lastValue = Number(last?.[trendTarget.key] || 0);
    if (!first || !last || firstValue <= 0) {
      return {
        variationPct: 0,
        noun: trendTarget.noun,
        label: `Mostrando evolucion de los ultimos ${rangeDays} dias`
      };
    }
    const variation = ((lastValue - firstValue) / firstValue) * 100;
    return {
      variationPct: variation,
      noun: trendTarget.noun,
      label: rangeMode === 'custom'
        ? `Mostrando evolucion del periodo personalizado`
        : `Mostrando evolucion de los ultimos ${rangeDays} dias`
    };
  }, [chartData, rangeDays, rangeMode, trendTarget]);

  const customTooltip = (props: any) => {
    const { active, payload, label } = props || {};
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="min-w-[220px] rounded-xl border border-border/70 bg-background/95 px-3 py-2 shadow-xl backdrop-blur">
        <div className="mb-1.5 text-xs font-medium text-muted-foreground">Fecha: {String(label || '-')}</div>
        <div className="space-y-1.5">
          {payload.map((entry: any) => {
            const key = String(entry.dataKey || '') as 'cost' | 'sale';
            const meta = SERIES_META[key];
            if (!meta) return null;
            return (
              <div key={key} className="flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
                  <span className="text-foreground">{meta.label}</span>
                </div>
                <span className="font-semibold tabular-nums">{formatMoney(Number(entry.value || 0))}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const salesChartData = useMemo(
    () =>
      (salesAnalytics.series || []).map((point) => ({
        ...point,
        dateLabel: point.date ? new Date(`${point.date}T00:00:00`).toLocaleDateString('es-AR') : '-'
      })),
    [salesAnalytics]
  );

  const salesTrend = useMemo(() => {
    const key = salesMode === 'units' ? 'units' : 'revenue';
    const noun = salesMode === 'units' ? 'la cantidad vendida' : 'la facturacion';
    const first = salesChartData[0];
    const last = salesChartData[salesChartData.length - 1];
    const firstValue = Number(first?.[key] || 0);
    const lastValue = Number(last?.[key] || 0);
    if (!first || !last || firstValue <= 0) {
      return {
        value: 0,
        noun,
        direction: 'flat' as 'up' | 'down' | 'flat'
      };
    }
    const variation = ((lastValue - firstValue) / firstValue) * 100;
    return {
      value: variation,
      noun,
      direction: variation > 0 ? 'up' as const : variation < 0 ? 'down' as const : 'flat' as const
    };
  }, [salesChartData, salesMode]);

  const salesTooltip = (props: any) => {
    const { active, payload, label } = props || {};
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div className="min-w-[220px] rounded-xl border border-border/70 bg-background/95 px-3 py-2 shadow-xl backdrop-blur">
        <div className="mb-1.5 text-xs font-medium text-muted-foreground">Fecha: {String(label || '-')}</div>
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Cantidad</span>
            <span className="font-semibold tabular-nums">{Number(row.units || 0).toLocaleString('es-AR')}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Facturacion</span>
            <span className="font-semibold tabular-nums">{formatMoney(Number(row.revenue || 0))}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Remitos</span>
            <span className="font-semibold tabular-nums">{Number(row.remitos || 0).toLocaleString('es-AR')}</span>
          </div>
        </div>
      </div>
    );
  };

  const loadEquivalences = React.useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`/api/products/${id}/equivalences`);
      setEquivalences(r.data?.equivalences || []);
    } catch { setEquivalences([]); }
  }, [id]);

  useEffect(() => { loadEquivalences(); }, [loadEquivalences]);

  // Catálogo para elegir el equivalente (se pide una sola vez)
  useEffect(() => {
    axios.get('/api/products?limit=all')
      .then(r => setEquivCatalog(r.data?.products || []))
      .catch(() => setEquivCatalog([]));
  }, []);

  const addEquivalence = async (prod: { id: string; name: string }) => {
    if (!id || !prod?.id) return;
    if (prod.id === id) { toast.error('Es el mismo producto'); return; }
    setSavingEquiv(true);
    try {
      await axios.post(`/api/products/${id}/equivalences`, { equivalentId: prod.id, note: equivNote.trim() || undefined });
      setEquivNote('');
      await loadEquivalences();
      toast.success(`Equivalencia agregada: ${prod.name}`);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo agregar la equivalencia');
    } finally { setSavingEquiv(false); }
  };

  const removeEquivalence = async (equivalentId: string) => {
    if (!id) return;
    try {
      await axios.delete(`/api/products/${id}/equivalences/${equivalentId}`);
      await loadEquivalences();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo quitar la equivalencia');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando ficha...</div>;
  }

  if (!product) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Producto no encontrado.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/products')}>
          Volver
        </Button>
      </div>
    );
  }

  const isService = product.category === 'servicio';
  const currentCost = Number(product.costPrice || 0);
  const currentSale = Number(product.salePrice || 0);
  const currentStock = Number(product.stock || 0);
  const grossMarginPct = currentSale > 0 ? ((currentSale - currentCost) / currentSale) * 100 : 0;
  const markupPct = currentCost > 0 ? ((currentSale - currentCost) / currentCost) * 100 : 0;
  const units90d = Number(analytics90d.totals?.units || 0);
  const revenue90d = Number(analytics90d.totals?.revenue || 0);
  const remitos90d = Number(analytics90d.totals?.remitos || 0);
  const dailyUnits90d = units90d / 90;
  const estimatedProfit90d = Math.max(0, currentSale - currentCost) * units90d;
  const coverageDays = !isService && dailyUnits90d > 0 ? currentStock / dailyUnits90d : null;
  const historySeries = analyticsHistory.series || [];
  const lastSaleDate = historySeries.length ? historySeries[historySeries.length - 1]?.date : null;
  const daysSinceLastSale = (() => {
    if (!lastSaleDate) return null;
    const parsed = new Date(`${lastSaleDate}T00:00:00`);
    if (!Number.isFinite(parsed.getTime())) return null;
    const diff = Date.now() - parsed.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  })();

  const kpiCards: KpiCard[] = isService
    ? [
        {
          label: 'Ticket promedio 90d',
          value: remitos90d > 0 ? formatMoney(revenue90d / remitos90d) : '-',
          hint: remitos90d > 0 ? `${remitos90d} comprobantes en 90 dias` : 'Sin ventas en 90 dias'
        },
        {
          label: 'Ventas 90d',
          value: Number(units90d || 0).toLocaleString('es-AR'),
          hint: revenue90d > 0 ? `${formatMoney(revenue90d)} facturados` : 'Sin facturacion reciente'
        },
        {
          label: 'Dias desde ultima venta',
          value: daysSinceLastSale === null ? '-' : `${daysSinceLastSale}`,
          hint: lastSaleDate ? `Ultima venta el ${new Date(`${lastSaleDate}T00:00:00`).toLocaleDateString('es-AR')}` : 'Sin ventas historicas'
        },
        {
          label: 'Precio promedio actual',
          value: formatMoney(currentSale),
          hint: currentCost > 0 ? `Costo actual ${formatMoney(currentCost)}` : 'Sin costo de referencia'
        }
      ]
    : [
        {
          label: 'Markup',
          value: currentCost > 0 ? formatPercent(markupPct) : '-',
          hint: currentCost > 0 ? `Costo ${formatMoney(currentCost)} → Venta ${formatMoney(currentSale)}` : 'Sin costo de referencia'
        },
        {
          label: 'Margen bruto',
          value: currentSale > 0 ? formatPercent(grossMarginPct) : '-',
          hint: currentSale > 0 ? `${formatMoney(currentSale - currentCost)} por unidad` : 'Sin precio de venta'
        },
        {
          label: 'Rentabilidad 90d',
          value: estimatedProfit90d > 0 ? formatMoney(estimatedProfit90d) : '$ 0,00',
          hint: units90d > 0 ? `${Number(units90d).toLocaleString('es-AR')} unidades estimadas` : 'Sin ventas en 90 dias'
        },
        {
          label: 'Cobertura de stock',
          value: coverageDays === null ? '-' : `${Math.round(coverageDays)} dias`,
          hint: dailyUnits90d > 0 ? `${dailyUnits90d.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unid/dia` : 'Sin rotacion reciente'
        },
        {
          label: 'Dias sin venta',
          value: daysSinceLastSale === null ? '-' : `${daysSinceLastSale}`,
          hint: lastSaleDate ? `Ultima venta el ${new Date(`${lastSaleDate}T00:00:00`).toLocaleDateString('es-AR')}` : 'Sin ventas historicas'
        }
      ];

  const activityChartData = (analytics90d.series || []).map((point) => ({
    ...point,
    dateLabel: point.date ? new Date(`${point.date}T00:00:00`).toLocaleDateString('es-AR') : '-',
    rotationLoad: !isService && currentStock > 0 ? Number(((Number(point.units || 0) / Math.max(currentStock, 1)) * 100).toFixed(2)) : 0
  }));

  const activityTooltip = (props: any) => {
    const { active, payload, label } = props || {};
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div className="min-w-[220px] rounded-xl border border-border/70 bg-background/95 px-3 py-2 shadow-xl backdrop-blur">
        <div className="mb-1.5 text-xs font-medium text-muted-foreground">Fecha: {String(label || '-')}</div>
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">{isService ? 'Ventas' : 'Unidades'}</span>
            <span className="font-semibold tabular-nums">{Number(row.units || 0).toLocaleString('es-AR')}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">{isService ? 'Comprobantes' : 'Facturacion'}</span>
            <span className="font-semibold tabular-nums">
              {isService ? Number(row.remitos || 0).toLocaleString('es-AR') : formatMoney(Number(row.revenue || 0))}
            </span>
          </div>
          {!isService && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Rotacion sobre stock</span>
              <span className="font-semibold tabular-nums">{formatPercent(Number(row.rotationLoad || 0))}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const primaryInfo = [
    { label: 'SKU', value: product.sku || '-' },
    { label: 'Categoria', value: product.category || '-' },
    { label: 'Proveedor vigente', value: product.mainSupplierName || '-' },
    { label: 'Estado', value: product.status || '-' }
  ];

  const pricingInfo = [
    { label: 'Costo', value: formatMoney(product.costPrice) },
    { label: 'Precio venta', value: formatMoney(product.salePrice) },
    { label: 'Precio lista', value: formatMoneyOptional(product.listPrice) },
    { label: 'Stock', value: isService ? 'No aplica' : Number(product.stock || 0).toLocaleString('es-AR') },
    { label: 'Stock minimo', value: isService ? 'No aplica' : Number(product.minStock || 0).toLocaleString('es-AR') }
  ];

  const cat = String(product.category || '').toLowerCase();
  const isTire = cat === 'neumaticos' || cat === 'cubiertas';
  const isOil = cat === 'aceites' || cat === 'aceite';
  const isLubricant = cat === 'lubricantes' || cat === 'lubricante' || cat === 'liquidos' || cat === 'grasas';
  const isPart = cat === 'repuestos' || cat === 'repuesto';

  const skillsList = (() => {
    if (Array.isArray(product.requiredSkills)) return product.requiredSkills.join(', ');
    if (typeof product.requiredSkills === 'string') {
      try {
        const parsed = JSON.parse(product.requiredSkills);
        if (Array.isArray(parsed)) return parsed.join(', ');
      } catch {}
      return product.requiredSkills;
    }
    return '-';
  })();

  const technicalInfo: Array<{ label: string; value: string; fullWidth?: boolean }> = [];
  if (!isService) {
    technicalInfo.push({ label: 'Marca', value: product.brand || '-' });
    technicalInfo.push({ label: 'Modelo', value: product.model || '-' });
  }
  if (isTire) {
    technicalInfo.push({ label: 'Medida', value: product.tireMeasure || '-' });
    technicalInfo.push({ label: 'Indice carga', value: product.loadIndex || '-' });
    technicalInfo.push({ label: 'Indice velocidad', value: product.speedIndex || '-' });
  }
  if (isOil) {
    technicalInfo.push({ label: 'Viscosidad', value: product.viscosity || '-' });
    technicalInfo.push({ label: 'Tipo de base', value: product.oilBaseType || '-' });
    technicalInfo.push({ label: 'Capacidad', value: product.capacity || '-' });
    technicalInfo.push({ label: 'Spec API', value: product.apiSpec || '-' });
  }
  if (isLubricant) {
    technicalInfo.push({ label: 'Tipo', value: product.lubricantType || '-' });
    technicalInfo.push({ label: 'Capacidad', value: product.capacity || '-' });
  }
  if (isPart) {
    technicalInfo.push({ label: 'Numero de parte', value: product.partNumber || '-' });
  }
  if (!isService) {
    // La compatibilidad se guarda como lista: se muestra legible, no el JSON
    const autos = parseFitment(product.vehicleFitment).map(etiquetaFitment).filter(Boolean);
    technicalInfo.push({
      label: autos.length > 1 ? 'Vehículos compatibles' : 'Vehículo compatible',
      value: autos.length ? autos.join(' · ') : '-',
      fullWidth: true
    });
  }
  if (isService) {
    technicalInfo.push({ label: 'Duracion estimada', value: product.estimatedDuration ? `${product.estimatedDuration} min` : '-' });
    technicalInfo.push({ label: 'Rol requerido', value: product.requiredRole || '-' });
    technicalInfo.push({ label: 'Skills requeridas', value: skillsList || '-', fullWidth: true });
  }
  technicalInfo.push({ label: 'Descripcion', value: product.description || '-', fullWidth: true });

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Ficha de producto/servicio</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/products')}>Volver</Button>
          <Button onClick={() => navigate(`/products/${product.id}/edit`)}>Editar</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <CardTitle className="text-2xl leading-tight">{product.name}</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-[0.18em]">
                  {product.category || 'Sin categoria'}
                </Badge>
                <Badge
                  className={
                    product.status === 'active'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-700 text-white'
                  }
                >
                  {product.status || 'Sin estado'}
                </Badge>
                {!isService && Number(product.stock || 0) === 0 ? (
                  <Badge variant="outline" className="border-amber-500/60 text-amber-300">
                    Sin stock
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="grid min-w-[260px] grid-cols-2 gap-3 lg:max-w-[360px]">
              {primaryInfo.map((item) => (
                <div key={item.label} className="rounded-xl border border-border/70 bg-muted/20 p-3">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
                  <p className="mt-1 text-sm font-semibold leading-snug">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Comercial
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              {pricingInfo.map((item) => (
                <div key={item.label} className="rounded-xl border border-border/70 bg-background/40 p-4">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
                  <p className="mt-2 text-xl font-semibold leading-none tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Normas y aprobaciones: cada una es buscable, así se encuentra el
              aceite por lo que pide el manual del auto */}
          {Array.isArray((product as any).tags) && (product as any).tags.length > 0 && (
            <div className="mb-6">
              <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Normas y aprobaciones
              </p>
              <div className="flex flex-wrap gap-2">
                {(product as any).tags.map((t: string) => (
                  <Tip key={t} label="Buscar productos con esta norma">
                    <button
                      type="button"
                      onClick={() => navigate(`/products?q=${encodeURIComponent(t)}`)}
                      className="rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium transition-colors hover:border-primary hover:text-primary"
                    >
                      {t}
                    </button>
                  </Tip>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Ficha tecnica
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              {technicalInfo.map((item) => (
                <div
                  key={item.label}
                  className={`rounded-xl border border-border/70 bg-background/40 p-4 ${
                    item.fullWidth ? 'xl:col-span-2' : ''
                  }`}
                >
                  <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
                  <p className="mt-2 text-sm font-semibold leading-relaxed">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {!isService && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Equivalencias</CardTitle>
            <p className="text-sm text-muted-foreground">
              Otros artículos que reemplazan a este (mismo filtro de otra marca, código alternativo...).
              Se guarda en los dos sentidos: también aparece en la ficha del otro producto.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-2 md:flex-row">
              <div className="flex-1 min-w-0">
                <ProductCombobox
                  products={equivCatalog
                    .filter((p: any) => p.id !== id && !equivalences.some((e: any) => e.productId === p.id))
                    .map((p: any) => ({
                      id: p.id,
                      name: p.name,
                      sellingPrice: Number(p.salePrice || p.sellingPrice || 0),
                      category: p.category ?? '',
                      isService: p.isService ?? false,
                      sku: p.sku,
                      stock: p.stock,
                    }))}
                  selectedProduct={null}
                  onSelect={(p: any) => addEquivalence(p)}
                  placeholder="Buscar el artículo equivalente..."
                />
              </div>
              <Input
                value={equivNote}
                onChange={(e) => setEquivNote(e.target.value)}
                placeholder="Nota (opcional): ej. mismo filtro, marca alternativa"
                className="md:w-72"
                disabled={savingEquiv}
              />
            </div>

            {equivalences.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Sin equivalencias cargadas. Buscá arriba el artículo que lo reemplaza.
              </p>
            ) : (
              <div className="cq-table rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b">
                      <TableHead className="h-9 px-3 text-xs">Artículo</TableHead>
                      <TableHead className="h-9 px-3 text-xs cq-hide-narrow">Marca</TableHead>
                      <TableHead className="h-9 px-3 text-xs cq-hide-narrow">Código</TableHead>
                      <TableHead className="h-9 px-3 text-xs text-right">Stock</TableHead>
                      <TableHead className="h-9 px-3 text-xs text-right">Precio</TableHead>
                      <TableHead className="h-9 px-3 text-xs cq-hide-narrow">Nota</TableHead>
                      <TableHead className="h-9 w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equivalences.map((e: any) => (
                      <TableRow key={e.productId} className="border-b hover:bg-muted/30">
                        <TableCell className="px-3 py-2">
                          <button
                            type="button"
                            className="text-left text-sm font-medium hover:underline"
                            onClick={() => navigate(`/products/${e.productId}/view`)}
                          >
                            {e.name}
                          </button>
                        </TableCell>
                        <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{e.brand || '—'}</TableCell>
                        <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{e.sku || '—'}</TableCell>
                        <TableCell className={`px-3 py-2 text-sm text-right ${Number(e.stock || 0) > 0 ? '' : 'text-amber-600'}`}>
                          {Number(e.stock || 0)}
                        </TableCell>
                        <TableCell className="px-3 py-2 text-sm text-right tabular-nums">{formatMoney(Number(e.salePrice || e.listPrice || 0))}</TableCell>
                        <TableCell className="px-3 py-2 text-xs text-muted-foreground cq-hide-narrow cq-clamp-narrow truncate">{e.note || '—'}</TableCell>
                        <TableCell className="px-2 py-2 text-right">
                          <Tip label="Quitar equivalencia">
                            <button
                              type="button"
                              onClick={() => removeEquivalence(e.productId)}
                              className="rounded p-1 text-muted-foreground hover:text-red-500"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </Tip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mb-4 mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        {kpiCards.map((kpi) => (
          <Card key={kpi.label} className="border-border/70">
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {kpi.label}
              </p>
              <p className="mt-2 text-2xl font-semibold tracking-tight">{kpi.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{kpi.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{isService ? 'Frecuencia del servicio 90d' : 'Rotacion y salida 90d'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 overflow-hidden rounded-lg border md:grid-cols-3">
            <div className="border-b p-4 md:border-b-0 md:border-r">
              <p className="text-base font-semibold">{isService ? 'Demanda reciente' : 'Salida reciente'}</p>
              <p className="text-sm text-muted-foreground">
                {isService
                  ? 'Frecuencia de venta del servicio en los ultimos 90 dias'
                  : 'Unidades vendidas y peso sobre el stock actual en los ultimos 90 dias'}
              </p>
            </div>
            <div className="border-b p-4 md:border-b-0 md:border-r">
              <p className="text-xs text-muted-foreground">{isService ? 'Comprobantes 90d' : 'Unidades 90d'}</p>
              <p className="text-2xl font-bold tabular-nums">
                {Number(isService ? remitos90d : units90d).toLocaleString('es-AR')}
              </p>
            </div>
            <div className="p-4">
              <p className="text-xs text-muted-foreground">{isService ? 'Facturacion 90d' : 'Promedio diario'}</p>
              <p className="text-2xl font-bold tabular-nums">
                {isService
                  ? formatMoney(revenue90d)
                  : dailyUnits90d.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityChartData} margin={{ top: 8, right: 16, left: 4, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={88}
                  tickFormatter={(value) =>
                    isService
                      ? Number(value || 0).toLocaleString('es-AR')
                      : Number(value || 0).toLocaleString('es-AR')
                  }
                />
                <Tooltip
                  content={activityTooltip}
                  cursor={{ fill: 'hsl(var(--accent) / 0.15)' }}
                />
                <Bar
                  dataKey={isService ? 'remitos' : 'units'}
                  name={isService ? 'Frecuencia' : 'Unidades'}
                  radius={[6, 6, 0, 0]}
                  fill={isService ? 'hsl(var(--chart-2))' : 'hsl(var(--chart-1))'}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Proveedores y costos</CardTitle>
        </CardHeader>
        <CardContent>
          {mergedProviderRows.length ? (
            <div className="space-y-2">
              {mergedProviderRows.map((row) => {
                const isVigente =
                  Number(row.costPrice || 0) === Number(product.costPrice || 0) &&
                  String(row.providerName || '').trim().toLowerCase() === String(product.mainSupplierName || '').trim().toLowerCase();
                const isHistoricalOnly = String(row.id || '').startsWith('history-');
                return (
                  <div
                    key={row.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                      isVigente ? 'border-green-500/60 bg-green-500/10' : ''
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.providerName || 'Sin proveedor'}</span>
                        {row.isFavorite ? <Badge variant="secondary">Favorito</Badge> : null}
                        {isHistoricalOnly ? <Badge variant="secondary">Historico</Badge> : null}
                        {isVigente ? <Badge className="bg-green-600 text-white">Vigente</Badge> : null}
                      </div>
                      {row.supplierSku ? (
                        <span className="text-xs text-muted-foreground">
                          SKU prov.: <span className="font-mono">{row.supplierSku}</span>
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatMoney(row.costPrice)}</div>
                      <div className="text-xs text-muted-foreground">
                        Lista: {formatMoneyOptional(row.listPrice)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('es-AR') : '-'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin costos por proveedor registrados.</p>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Evolución de precios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 overflow-hidden rounded-lg border md:grid-cols-3">
            <div className="border-b p-4 md:col-span-1 md:border-b-0 md:border-r">
              <p className="text-base font-semibold">Lineas de precios</p>
              <p className="text-sm text-muted-foreground">{trendStats.label}</p>
            </div>
            <button
              type="button"
              className={`border-b p-4 text-left transition-colors md:border-b-0 md:border-r ${focusSeries === 'cost' ? 'bg-accent/50' : 'hover:bg-accent/20'}`}
              onClick={() => setFocusSeries((prev) => (prev === 'cost' ? 'all' : 'cost'))}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: SERIES_META.cost.color }}
                />
                Costo actual
              </p>
              <p className="text-2xl font-bold tabular-nums">{formatMoney(product.costPrice)}</p>
            </button>
            <button
              type="button"
              className={`p-4 text-left transition-colors ${focusSeries === 'sale' ? 'bg-accent/50' : 'hover:bg-accent/20'}`}
              onClick={() => setFocusSeries((prev) => (prev === 'sale' ? 'all' : 'sale'))}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: SERIES_META.sale.color }}
                />
                Precio venta
              </p>
              <p className="text-2xl font-bold tabular-nums">{formatMoney(product.salePrice)}</p>
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-1 rounded-lg border p-1">
              {[30, 90, 180, 365].map((days) => (
                <Button
                  key={days}
                  type="button"
                  variant={rangeMode === 'preset' && rangeDays === days ? 'default' : 'ghost'}
                  className="h-8 px-3"
                  onClick={() => {
                    setRangeMode('preset');
                    setRangeDays(days as 30 | 90 | 180 | 365);
                  }}
                >
                  {days}d
                </Button>
              ))}
              <Button
                type="button"
                variant={rangeMode === 'custom' ? 'default' : 'ghost'}
                className="h-8 px-3"
                onClick={() => setRangeMode('custom')}
              >
                Fechas
              </Button>
            </div>
            <div />
          </div>

          {availableProviders.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Proveedores:</span>
              <button
                type="button"
                onClick={() => setSelectedProviders([])}
                className={`h-7 rounded-full border px-3 text-xs transition-colors ${
                  selectedProviders.length === 0
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent'
                }`}
              >
                Todos
              </button>
              {availableProviders.map((prov) => {
                const active = selectedProviders.includes(prov);
                return (
                  <button
                    key={prov}
                    type="button"
                    onClick={() => {
                      setSelectedProviders((prev) =>
                        prev.includes(prov) ? prev.filter((p) => p !== prov) : [...prev, prov]
                      );
                    }}
                    className={`h-7 rounded-full border px-3 text-xs transition-colors ${
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-accent'
                    }`}
                  >
                    {prov}
                  </button>
                );
              })}
            </div>
          )}

          {rangeMode === 'custom' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Desde</label>
                <DatePicker
                  value={customFrom}
                  onChange={setCustomFrom}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Hasta</label>
                <DatePicker
                  value={customTo}
                  onChange={setCustomTo}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
            </div>
          )}

          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 12, right: 16, left: 4, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickFormatter={(value) => `$${Number(value || 0).toLocaleString('es-AR')}`}
                  tickLine={false}
                  axisLine={false}
                  width={88}
                />
                <Tooltip
                  content={customTooltip}
                  cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeOpacity: 0.35, strokeDasharray: '3 3' }}
                />
                {focusSeries !== 'sale' && (
                  <Line
                    type="monotone"
                    dataKey="cost"
                    name={SERIES_META.cost.label}
                    stroke={SERIES_META.cost.color}
                    strokeWidth={focusSeries === 'cost' || focusSeries === 'all' ? 2.6 : 1.6}
                    strokeOpacity={focusSeries === 'all' || focusSeries === 'cost' ? 1 : 0.45}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 1, fill: SERIES_META.cost.color }}
                  />
                )}
                {focusSeries !== 'cost' && (
                  <Line
                    type="monotone"
                    dataKey="sale"
                    name={SERIES_META.sale.label}
                    stroke={SERIES_META.sale.color}
                    strokeWidth={focusSeries === 'sale' || focusSeries === 'all' ? 2.6 : 1.6}
                    strokeOpacity={focusSeries === 'all' || focusSeries === 'sale' ? 1 : 0.45}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 1, fill: SERIES_META.sale.color }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="border-t pt-3">
            <p className="text-sm font-medium">
              {trendStats.variationPct >= 0 ? 'Sube' : 'Baja'} {Math.abs(trendStats.variationPct).toFixed(2)}% {trendStats.noun} en este periodo
            </p>
            <p className="text-sm text-muted-foreground">
              Referencia basada en el primer y ultimo valor del rango seleccionado.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>{isService ? 'Demanda del servicio' : 'Ventas del item'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 overflow-hidden rounded-lg border md:grid-cols-3">
            <div className="border-b p-4 md:border-b-0 md:border-r">
              <p className="text-base font-semibold">Grafico de area</p>
              <p className="text-sm text-muted-foreground">
                {rangeMode === 'custom'
                  ? 'Periodo personalizado'
                  : `Ultimos ${rangeDays} dias`}
              </p>
            </div>
            <button
              type="button"
              className={`border-b p-4 text-left transition-colors md:border-b-0 md:border-r ${salesMode === 'units' ? 'bg-accent/50' : 'hover:bg-accent/20'}`}
              onClick={() => setSalesMode('units')}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-1))' }} />
                Cantidad vendida
              </p>
              <p className="text-2xl font-bold tabular-nums">
                {Number(salesAnalytics.totals?.units || 0).toLocaleString('es-AR')}
              </p>
            </button>
            <button
              type="button"
              className={`p-4 text-left transition-colors ${salesMode === 'revenue' ? 'bg-accent/50' : 'hover:bg-accent/20'}`}
              onClick={() => setSalesMode('revenue')}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-2))' }} />
                Facturación total
              </p>
              <p className="text-2xl font-bold tabular-nums">
                {formatMoney(Number(salesAnalytics.totals?.revenue || 0))}
              </p>
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-1 rounded-lg border p-1">
              {[30, 90, 180, 365].map((days) => (
                <Button
                  key={`sales-${days}`}
                  type="button"
                  variant={rangeMode === 'preset' && rangeDays === days ? 'default' : 'ghost'}
                  className="h-8 px-3"
                  onClick={() => {
                    setRangeMode('preset');
                    setRangeDays(days as 30 | 90 | 180 | 365);
                  }}
                >
                  {days}d
                </Button>
              ))}
              <Button
                type="button"
                variant={rangeMode === 'custom' ? 'default' : 'ghost'}
                className="h-8 px-3"
                onClick={() => setRangeMode('custom')}
              >
                Fechas
              </Button>
            </div>
            <div />
          </div>

          {rangeMode === 'custom' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Desde</label>
                <DatePicker
                  value={customFrom}
                  onChange={setCustomFrom}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Hasta</label>
                <DatePicker
                  value={customTo}
                  onChange={setCustomTo}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
            </div>
          )}

          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesChartData} margin={{ top: 8, right: 16, left: 4, bottom: 8 }}>
                <defs>
                  <linearGradient id="salesAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={salesMode === 'units' ? 'hsl(var(--chart-1))' : 'hsl(var(--chart-2))'}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="95%"
                      stopColor={salesMode === 'units' ? 'hsl(var(--chart-1))' : 'hsl(var(--chart-2))'}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={88}
                  tickFormatter={(value) =>
                    salesMode === 'units'
                      ? Number(value || 0).toLocaleString('es-AR')
                      : `$${Number(value || 0).toLocaleString('es-AR')}`
                  }
                />
                <Tooltip
                  content={salesTooltip}
                  cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeOpacity: 0.35, strokeDasharray: '3 3' }}
                />
                <Area
                  type="monotone"
                  dataKey={salesMode === 'units' ? 'units' : 'revenue'}
                  stroke={salesMode === 'units' ? 'hsl(var(--chart-1))' : 'hsl(var(--chart-2))'}
                  fill="url(#salesAreaGradient)"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 1 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="border-t pt-3">
            <p className="text-sm font-medium">
              {salesTrend.direction === 'up' ? 'Sube' : salesTrend.direction === 'down' ? 'Baja' : 'Se mantiene'}{' '}
              {Math.abs(salesTrend.value).toFixed(2)}% {salesTrend.noun} en este periodo
            </p>
            <p className="text-sm text-muted-foreground">
              Referencia basada en el primer y ultimo valor del rango seleccionado.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Historial de costos</CardTitle>
        </CardHeader>
        <CardContent>
          {costHistory.length ? (
            <div className="space-y-2">
              {costHistory.map((entry) => {
                const inferredProvider = inferHistoryProvider(entry);
                const isVigente = entry.id === vigenteHistoryId;
                return (
                  <div
                    key={entry.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                      isVigente ? 'border-green-500/60 bg-green-500/10' : ''
                    }`}
                  >
                    <span className={isVigente ? 'font-semibold' : ''}>
                      {formatMoney(entry.costPrice)}
                      {entry.previousCostPrice !== undefined && entry.previousCostPrice !== null
                        ? ` (antes ${formatMoney(entry.previousCostPrice)})`
                        : ''}
                      {inferredProvider ? ` - ${inferredProvider}` : ''}
                      {isVigente ? ' - VIGENTE' : ''}
                    </span>
                    <span className="text-muted-foreground">{new Date(entry.createdAt).toLocaleDateString('es-AR')}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin historial de costos.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductView;
